import classNames from "classnames";
import { ValidationSummaryText } from "Components/ValidationSummaryText";
import {
  Formik,
  FormikProps,
  FormikValues,
  getIn,
  setIn,
  useFormikContext,
} from "formik";
import React, { useRef, createContext, RefObject, useContext } from "react";
import { Assert } from "../Tools";
import { ModelStateErrors, ModelStateValues } from "./Tools";

const FormRefContext = createContext<RefObject<HTMLFormElement>>({
  current: null,
});

export interface MVCFormServerProps {
  antiForgeryToken: string;
  initialValues: ModelStateValues;
  initialErrors: ModelStateErrors;
  action: string;
}

interface MVCFormProps extends MVCFormServerProps {
  encType?: string;
  children: (props: FormikProps<FormikValues>) => React.ReactNode;
  renderValidationSummary?: (errors: {
    [K: string]: string;
  }) => React.ReactNode;
  shouldSubmit?: (values: FormikValues) => boolean;
}

const submitFieldName = "submit";

function nestedEntries(o: { [k: string]: any }, prefix = ""): [string, any][] {
  return Object.entries(o).flatMap(([k, v]) => {
    if (v?.constructor === Object) {
      return nestedEntries(v, `${prefix}${k}.`);
    } else {
      return [[`${prefix}${k}`, v]];
    }
  });
}

export function MVCForm(props: MVCFormProps) {
  const formRef = useRef<HTMLFormElement>(null);
  const initialValues = nestedEntries(props.initialValues).reduce(
    (acc, [k, v]) => setIn(acc, k, v?.toString()),
    {},
  );
  const initialErrors = nestedEntries(props.initialErrors).reduce(
    (acc, [k, v]) => (v.length === 0 ? acc : setIn(acc, k, v[0])),
    {},
  );
  const renderValidationSummary = (errors: { [K: string]: string }) =>
    props.renderValidationSummary
      ? props.renderValidationSummary(errors)
      : Object.values(errors).length > 0 && <ValidationSummaryText />;

  return (
    <Formik<any>
      onSubmit={async values => {
        if (props.shouldSubmit && !props.shouldSubmit(values)) {
          return;
        }
        await submitFormRef(formRef, getIn(values, submitFieldName) || "");
      }}
      initialValues={initialValues}
      initialErrors={initialErrors}
    >
      {formProps => (
        <FormRefContext.Provider value={formRef}>
          <form
            method="post"
            encType={props.encType}
            action={props.action}
            ref={formRef}
            onSubmitCapture={async e => {
              e.preventDefault();
              const source = document.activeElement;
              if (
                source instanceof HTMLButtonElement ||
                source instanceof HTMLInputElement
              ) {
                // NOTE: we want this form element to be an enter key catcher,
                // our submits should go through formik
                Assert.hard(
                  source.type !== "submit",
                  `form submit called directly with name=${source.name}`,
                );
              }
              await submitFormWithValue(formProps, formRef, "");
            }}
            onResetCapture={formProps.handleReset}
          >
            <button type="submit" className="invisible-button" />
            <input name={submitFieldName} type="hidden" />
            <input
              name="__RequestVerificationToken"
              type="hidden"
              value={props.antiForgeryToken}
            />
            {renderValidationSummary(initialErrors)}
            {props.children(formProps)}
          </form>
        </FormRefContext.Provider>
      )}
    </Formik>
  );
}

async function submitFormRef(
  formRef: RefObject<HTMLFormElement>,
  value: string,
) {
  if (formRef.current) {
    // NOTE: bypasses onSubmit below to submit for real
    (formRef.current[submitFieldName] as any).value = value;
    HTMLFormElement.prototype.submit.call(formRef.current);
    // NOTE: never resolves since the above call should always POST to server and get a new page
    await new Promise(() => undefined);
  }
}

async function submitFormWithValue(
  formik: FormikProps<FormikValues>,
  formRef: RefObject<HTMLFormElement>,
  value: string,
  skipValidation = false,
) {
  if (!formik.isSubmitting) {
    formik.setFieldValue(submitFieldName, value);
    formik.setSubmitting(true);
    return skipValidation ? submitFormRef(formRef, value) : formik.submitForm();
  }
}

function useFormikSubmitWithValue(options: {
  value: string;
  isPrimary?: true;
}) {
  const formik = useFormikContext<any>();
  const formRef = useContext(FormRefContext);
  const field = formik.getFieldProps({ name: submitFieldName });
  return {
    isSubmitting: formik.isSubmitting,
    isThisValueSubmitting:
      formik.isSubmitting &&
      (field.value === options.value || (options.isPrimary && !field.value)),
    submit: () =>
      submitFormWithValue(formik, formRef, options.value, !options.isPrimary),
  };
}

export function MVCSubmitButton({
  isPrimary,
  ...props
}: React.DetailedHTMLProps<
  React.ButtonHTMLAttributes<HTMLButtonElement>,
  HTMLButtonElement
> & {
  value: string;
  isPrimary?: true;
}) {
  const { isSubmitting, isThisValueSubmitting, submit } =
    useFormikSubmitWithValue({ value: props.value, isPrimary });
  return (
    <button
      {...props}
      type="button"
      className={classNames(props.className, {
        "is-loading": isThisValueSubmitting,
      })}
      onClick={submit}
      disabled={isSubmitting || props.disabled}
    />
  );
}
